/**
 * Hook for check-in existing flow (working doc from OneDrive -> IFS).
 * Uses checkinExistingFlowSlice; optionally deletes OneDrive doc after success via existing delete API.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useLogin } from "./useLogin";
import {
  performCheckinExisting,
  clearCheckinExistingFlow,
  CHECKIN_EXISTING_STATUS,
} from "../slices/documents/checkinExistingFlowSlice";

export function useCheckinExistingFlow() {
  const dispatch = useAppDispatch();
  const { teamsContext } = useLogin();
  const state = useAppSelector(
    (rootState) => rootState.DocumentsSlice?.checkinExistingFlow
  ) ?? {
    status: CHECKIN_EXISTING_STATUS.IDLE,
    step: CHECKIN_EXISTING_STATUS.DOWNLOADING,
    message: null,
    loading: false,
    success: false,
    fileSizeBytes: 0,
    startedAt: null,
  };

  const runCheckinExisting = useCallback(
    async (params) => {
      if (!teamsContext?.user?.tenant?.id) {
        throw new Error("Teams context is not available");
      }
      const tenantId = teamsContext.user.tenant.id;
      const teamId =
        teamsContext.channel?.membershipType === "Private"
          ? teamsContext.channel?.ownerGroupId
          : teamsContext.team?.groupId ?? "";
      const channelId = teamsContext.channel?.id ?? "";
      const membershipType = teamsContext.channel?.membershipType ?? "Regular";
      const oneDriveRoot =
        teamId && channelId ? "ChannelDoc" : "MainMenu";

      return dispatch(
        performCheckinExisting({
          ...params,
          teamId,
          channelId,
          tenantId,
          oneDriveRoot,
          membershipType,
        })
      ).unwrap();
    },
    [dispatch, teamsContext]
  );

  const clear = useCallback(
    () => dispatch(clearCheckinExistingFlow()),
    [dispatch]
  );

  return {
    status: state.status ?? CHECKIN_EXISTING_STATUS.IDLE,
    step: state.step ?? CHECKIN_EXISTING_STATUS.DOWNLOADING,
    message: state.message ?? null,
    loading: state.loading ?? false,
    success: state.success ?? false,
    /** Revision info of the last successfully checked-in doc (for highlighting in All Documents). */
    lastDocumentRevision: state.lastDocumentRevision ?? null,
    runCheckinExisting,
    clear,
    CHECKIN_EXISTING_STATUS,
  };
}
